import React from 'react';
import {debounce} from 'ts-debounce';

import {AnalysisGraphQL, GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {GetChartViewDataFrame_dataFrame_DataFrameNode as VisualizationStepEditorDataFrameNode} from 'analysis-tool/ui/main-view/__generated/GetChartViewDataFrame';
import {assert} from 'Asserts';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {createRenderExpandTableButton} from 'data-frames/toolbar/toolbar-actions/renderExpandTableButton';
import DataFrameTableWrapper from 'data-frames/wrapper/DataFrameTableWrapper';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {getDefaultVisualizationNameForVisualizationType} from 'insights/analytics-visualizations/getDefaultVisualizationNameForVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {isStepConfigEditorModalSupported} from 'pipelines/generalized-step-config-editor/StepConfigEditorModal';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import * as PipelineDAO from 'pipelines/PipelineDAO';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {SaveState} from 'ui-library/SaveStatusIndicator';

import AdditionalEditorSection from '../components/AdditionalEditorSection';
import AddTransformStepDropdown, {
  BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE,
  FIX_FAILED_TRANSFORMS_MESSAGE,
  WAITING_FOR_STEP_SAVE_MESSAGE,
} from './AddTransformStepDropdown';
import VisualizationEditorSkeleton from './editor-sections/VisualizationEditorSkeleton';
import getDatasetViewErrorHandlingOptions from './getDatasetViewErrorHandlingOptions';
import getPersistedVisualizationConfig from './getPersistedVisualizationConfig';
import {getTransformPills} from './TransformPillUtils';

const visualizationConfigState = makeUseGlobalClientState<VisualizationConfig | null>(null);
const visualizationNameState = makeUseGlobalClientState<string | null>(null);

const visualizationSaveGlobalState = makeUseGlobalClientState<
  SaveState.SAVED | SaveState.SAVING | SaveState.FAILED
>(SaveState.SAVED);

function hasModalSupportedTransformStep(steps: Array<GetAnalysisPipelineStep>): boolean {
  return (
    steps.filter((step) =>
      isStepConfigEditorModalSupported(step.definition.stepType, step.definition.stepVersion)
    ).length > 0
  );
}

/** @package */
export const debouncedUpdateVisualizationConfig = debounce(
  (clientStateId: string, analysisId: string, stepId: string, config: VisualizationConfig) => {
    PipelineDAO.updateAndRunPipelineStep(analysisId, {
      stepId,
      stepConfig: {visualizationConfig: getPersistedVisualizationConfig(config)},
    }).then(
      () => {
        visualizationSaveGlobalState.updateState(clientStateId, SaveState.SAVED);
      },
      () => {
        visualizationSaveGlobalState.updateState(clientStateId, SaveState.FAILED);
      }
    );
  },
  200
);

/** @package */
export const debouncedUpdateVisualizationName = debounce(
  (clientStateId: string, analysisId: string, stepId: string, name: string) => {
    PipelineDAO.updateAndRunPipelineStep(analysisId, {
      stepId,
      stepConfig: {visualizationName: name},
    }).then(
      () => {
        visualizationSaveGlobalState.updateState(clientStateId, SaveState.SAVED);
      },
      () => {
        visualizationSaveGlobalState.updateState(clientStateId, SaveState.FAILED);
      }
    );
  },
  200
);

function renderPreviewDataFrames(
  state: {
    clientStateId: string;
    analysis: AnalysisGraphQL;
    viewSteps: Array<GetAnalysisPipelineStep>;
    visualizationType: VisualizationType | null;
  },
  dataFrameNode: VisualizationStepEditorDataFrameNode,
  isExpandedPreviewState: boolean,
  setIsExpandedPreviewState: (newState: boolean) => void
): JSX.Element {
  const {clientStateId, analysis, viewSteps, visualizationType} = state;

  const errorMessage = getDatasetViewErrorHandlingOptions(viewSteps, !analysis.permissions.writable);

  if (visualizationType !== VisualizationType.TABLE && errorMessage == null) {
    return (
      <DataFrameTableWrapper
        actionOrigin={ActionOrigin.ANALYSIS_TOOL}
        clientStateId={`${clientStateId}-table-preview`}
        dataFrame={dataFrameNode.randomSubsetDataFrameJson}
        dataFrameId={dataFrameNode.id}
        renderIsExcludedColumn={false}
        additionalElements={[
          createRenderExpandTableButton(isExpandedPreviewState, setIsExpandedPreviewState),
        ]}
      />
    );
  }

  return <></>;
}

function renderEditorSidebarChildren(
  state: {
    analysis: AnalysisGraphQL;
    viewSteps: Array<GetAnalysisPipelineStep>;
    canAddNewSteps: boolean;
    addNewStepsDisabledMessage?: string;
    inputDataFrameId: string;
    visualizationConfig: VisualizationConfig | null;
    visualizationType: VisualizationType | null;
    visualizationSaveState: SaveState;
  },
  dataFrameNode: VisualizationStepEditorDataFrameNode
): JSX.Element {
  const {
    analysis,
    viewSteps,
    inputDataFrameId,
    visualizationType,
    canAddNewSteps,
    addNewStepsDisabledMessage,
  } = state;

  return (
    <>
      <AdditionalEditorSection
        title="Filters and Transformations"
        addItemElement={
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={
              visualizationType === VisualizationType.TABLE
                ? dataFrameNode.dataFrameJson
                : dataFrameNode.randomSubsetDataFrameJson
            }
            inputDataFrameId={inputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        }
        pills={getTransformPills(analysis, viewSteps)}
        showTopBorder={visualizationType !== VisualizationType.TABLE}
      />
    </>
  );
}

export interface VisualizationStepEditorProps {
  analysis: AnalysisGraphQL;
  viewSteps: Array<GetAnalysisPipelineStep>;
  clientStateId: string;
  inputDataFrameId: string;
  handleSaveChart: (visualizationConfig: VisualizationConfig, visualizationName: string) => Promise<void>;
  initialVisualizationConfig?: VisualizationConfig | null;
  initialVisualizationName?: string;
}

export default function VisualizationStepEditor({
  analysis,
  viewSteps,
  clientStateId,
  handleSaveChart,
  inputDataFrameId,
  initialVisualizationConfig,
  initialVisualizationName,
}: VisualizationStepEditorProps): JSX.Element | null {
  const [nullableVisualizationConfig, setVisualizationConfig] =
    visualizationConfigState.useGlobalClientState(clientStateId);
  const [nullableVisualizationName, setVisualizationName] =
    visualizationNameState.useGlobalClientState(clientStateId);
  const [visualizationSaveState, setVisualizationSaveState] =
    visualizationSaveGlobalState.useGlobalClientState(clientStateId);

  const visualizationConfig: AnalyticsVisualization | null =
    nullableVisualizationConfig ??
    initialVisualizationConfig ??
    (hasModalSupportedTransformStep(viewSteps)
      ? {
          __typename: 'AnalyticsVisualizationNode',
          id: 'stub-id',
          parameters: {__typename: 'AnalyticsVisualizationTableParametersNode', displayColumnsJson: null},
        }
      : null) ??
    null;
  const visualizationType = visualizationConfig == null ? null : getVisualizationType(visualizationConfig);
  const visualizationName =
    nullableVisualizationName ??
    initialVisualizationName ??
    (visualizationConfig != null
      ? getDefaultVisualizationNameForVisualizationType(visualizationConfig.parameters.__typename)
      : 'New view');

  const allStepsSucceeded = viewSteps.every((step) => step.statusCode === PipelineStepStatusCode.SUCCEEDED);

  const currentStepSaved = visualizationSaveState === SaveState.SAVED;
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);

  const canAddNewSteps = allStepsSucceeded && currentStepSaved && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (!currentStepSaved ? WAITING_FOR_STEP_SAVE_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  function onChangeVisualizationName(name: string): void {
    setVisualizationName(name);
    if (name.length > 0) {
      setVisualizationSaveState(SaveState.SAVING);
      debouncedUpdateVisualizationName(clientStateId, analysis.id, viewSteps[viewSteps.length - 1].id, name);
    }
  }

  function onChangeVisualizationConfig(config: VisualizationConfig): void {
    setVisualizationConfig(config);
    setVisualizationSaveState(SaveState.SAVING);
    debouncedUpdateVisualizationConfig(
      clientStateId,
      analysis.id,
      viewSteps[viewSteps.length - 1].id,
      config
    );
  }

  return (
    <VisualizationEditorSkeleton
      clientStateId={clientStateId}
      analysis={analysis}
      viewSteps={viewSteps}
      canAddNewSteps={canAddNewSteps}
      inputDataFrameId={inputDataFrameId}
      visualizationSaveState={visualizationSaveState}
      visualizationName={visualizationName}
      onChangeVisualizationName={onChangeVisualizationName}
      visualizationConfig={visualizationConfig}
      onChangeVisualizationConfig={onChangeVisualizationConfig}
      renderPreviewDataFrames={(inputDataFrame, isExpandedPreviewState, setIsExpandedPreviewState) =>
        renderPreviewDataFrames(
          {clientStateId, analysis, viewSteps, visualizationType},
          inputDataFrame,
          isExpandedPreviewState,
          setIsExpandedPreviewState
        )
      }
      sidebarProps={{
        onClickDoneButton: () => {
          assert(
            visualizationConfig != null,
            'Visualization config should be non-null or button should be disabled.'
          );
          return handleSaveChart(visualizationConfig, visualizationName).then(() => {
            setVisualizationConfig(null);
            setVisualizationName(null);
          });
        },
        doneButtonIsDisabled: visualizationConfig == null || visualizationName === '',
      }}
      renderEditorSidebarChildren={(inputDataFrame) =>
        renderEditorSidebarChildren(
          {
            analysis,
            viewSteps,
            canAddNewSteps,
            addNewStepsDisabledMessage,
            inputDataFrameId,
            visualizationConfig,
            visualizationType,
            visualizationSaveState,
          },
          inputDataFrame
        )
      }
    />
  );
}
